import { AxiosInstance } from 'axios';
import { FinalReportResponseStatus } from 'core/finalReport/FinalReportStatus';
import { Order } from 'core/order/Order';
import {
  Metrics,
  OutdoorDeviceMetricType,
  OutdoorDeviceReportData,
  ReportCompareData,
  ReportCompareDimension,
  ReportData,
  ReportDimension,
  ReportGran,
  ReportType
} from 'core/report/ReportData';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import moment from 'moment';
import client, { clientWithoutErrorHandler } from './RestClient';
import { getL1ObjectChannelName } from 'core/channelGroup/channelDisplayName';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { FinalReportType } from 'core/finalReport/FinalReport';
import {
  WeatherVerificationCampaignOption,
  WeatherVerificationData,
  WeatherVerificationQuery,
  WeatherVerificationStoreOption,
  WeatherVerificationSummary,
  WeatherVerificationSummaryQuery
} from 'core/report/WeatherVerification';
import {
  DELIVERY_BRIEF_FILE_EXTENSION,
  DeliveryBriefFormat,
  DeliveryBriefMetric
} from 'core/report/DeliveryBriefReport';
import { parseContentDispositionFileName } from './DeviceManagementWebService';

export const defaultFormat = 'YYYY-MM-DD';

export interface ReportWebService {
  downloadOrderDeliveryBriefReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    format: DeliveryBriefFormat,
    metrics: DeliveryBriefMetric[]
  ): Promise<void>;
  downloadOrderReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void>;
  downloadL1ObjectReport(
    order: {
      name: string;
      l1ObjectId: number;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void>;
  downloadVideoReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadOutdoorReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadClosingReport(order: Order, reportType?: string): Promise<void>;
  downloadRebateReport(
    date: string,
    agencyName: string,
    agencyId?: string | number
  ): Promise<void>;
  getReportData(
    type: ReportType,
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string,
    useAppsflyerConversion?: boolean
  ): Promise<ReportData>;
  getOutdoorReportData(
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string
  ): Promise<ReportData>;
  getReportCompareData(
    compareDimension: ReportCompareDimension,
    filter: any,
    from: string,
    to: string
  ): Promise<ReportCompareData>;
  uploadFinalReportData(
    orderId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus>;
  getFinalReportData(
    reportId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus>;
  downloadOrderReportRawData(
    orderId: string | number,
    startDate: string,
    endDate: string
  ): Promise<void>;
  getFilterOptions(
    type: 'VIDEO' | 'PERFORMANCE',
    gran: 'daily' | 'hour',
    dimension: string,
    currentFilters: {
      [key: string]: string | number;
    }
  ): Promise<SelectOptions[]>;
  downloadSalesChannelReconciliationReport(
    gran: string,
    salesChannelId: number,
    from: string,
    to: string
  ): Promise<void>;
  downloadOrderOutdoorDeviceReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadOrderOutdoorDeviceBeaconReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void>;
  downloadOrderBeaconEventReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void>;
  getOutdoorDeviceReportData(
    l1ObjectId: number,
    from: string,
    to: string,
    metricType: OutdoorDeviceMetricType,
    gran: ReportGran,
    campaignId?: number,
    storeKeys?: string[]
  ): Promise<OutdoorDeviceReportData>;
  getWeatherVerificationCampaigns(
    l1ObjectId: number,
    from: string,
    to: string
  ): Promise<WeatherVerificationCampaignOption[]>;
  getWeatherVerificationStores(
    l1ObjectId: number,
    campaignId: number | undefined,
    from: string,
    to: string
  ): Promise<WeatherVerificationStoreOption[]>;
  getWeatherVerificationData(
    query: WeatherVerificationQuery
  ): Promise<WeatherVerificationData>;
  getWeatherVerificationSummary(
    query: WeatherVerificationSummaryQuery
  ): Promise<WeatherVerificationSummary>;
}

export class RestfulReportWebService implements ReportWebService {
  restClient: AxiosInstance;
  defualtFileDownload: any;
  /** 不掛全域錯誤處理（toast／錯誤頁）的 client，給失敗時由畫面自己提示的請求用 */
  quietClient: AxiosInstance;

  constructor(
    restClient: AxiosInstance = client,
    defualtFileDownload: any = fileDownload,
    quietClient: AxiosInstance = clientWithoutErrorHandler
  ) {
    this.restClient = restClient;
    this.defualtFileDownload = defualtFileDownload;
    this.quietClient = quietClient;
  }

  async downloadOrderReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void> {
    const url = `/v2/report/export/order?orderNum=${order.orderNumber}&groupBy=${groupBy}&metrics=${selectFields.join(',')}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, `${order.name}.xlsx`);
  }

  async downloadOrderDeliveryBriefReport(
    order: {
      name: string;
      orderNumber: string;
    },
    startDate: string,
    endDate: string,
    format: DeliveryBriefFormat,
    metrics: DeliveryBriefMetric[]
  ): Promise<void> {
    const params = [
      ['orderNum', order.orderNumber],
      ['from', startDate],
      ['to', endDate],
      ['format', format],
      ['metrics', metrics.join(',')]
    ]
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const response = await this.restClient.get(
      `/v2/report/export/order/delivery-brief?${params}`,
      { responseType: 'blob' }
    );
    // 檔名以後端的 Content-Disposition 為準（中文檔名只有它帶得過來）；
    // header 讀不到時才用訂單名稱。
    const headerFileName = parseContentDispositionFileName(
      response.headers?.['content-disposition']
    );
    this.defualtFileDownload(
      response.data,
      headerFileName ?? `${order.name}.${DELIVERY_BRIEF_FILE_EXTENSION[format]}`
    );
  }

  async downloadL1ObjectReport(
    l1Object: {
      name: string;
      l1ObjectId: number;
    },
    startDate: string,
    endDate: string,
    groupBy: string,
    selectFields: string[]
  ): Promise<void> {
    const url = `/v2/report/export/l1-object?l1ObjectId=${l1Object.l1ObjectId}&groupBy=${groupBy}&metrics=${selectFields.join(',')}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, `${l1Object.name}.xlsx`);
  }

  async downloadVideoReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void> {
    const response = await this.restClient.get(
      `report/video/export?orderNum=${order.orderNumber}&from=${startDate}&to=${endDate}`,
      { responseType: 'blob' }
    );
    this.defualtFileDownload(response.data, `${order.projectName}.xlsx`);
  }

  async downloadOutdoorReport(
    order: Order,
    startDate: string,
    endDate: string
  ): Promise<void> {
    const response = await this.restClient.get(
      `report/outdoor/export?orderNum=${order.orderNumber}&from=${startDate}&to=${endDate}`,
      { responseType: 'blob' }
    );
    this.defualtFileDownload(response.data, `${order.projectName}.xlsx`);
  }

  async downloadClosingReport(
    order: Order,
    reportType?: string
  ): Promise<void> {
    let url = `report/export/order/closing_report?orderNum=${order.orderNumber}`;
    if (reportType) {
      url = url + `&reportType=${reportType}`;
    }
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, `${order.projectName}.xlsx`);
  }

  async getReportData(
    type: ReportType,
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string,
    useAppsflyerConversion?: boolean
  ) {
    const filterQueryParams = Object.keys(filter)
      .map((key) => `${key}=${filter[key]}`)
      .join('&');
    const reportType =
      type === ReportType.PERFORMANCE ? 'channel-performance' : type;
    let url = `report/${reportType}?dimension=${dimension}&gran=${gran}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&${filterQueryParams}`;
    if (!_.isNil(useAppsflyerConversion)) {
      url = `${url}&useAppsflyerConversion=${useAppsflyerConversion}`;
    }
    const response = await this.restClient.get(url);
    const reportData = wrapReportData(response.data);
    if (dimension === ReportDimension.SALES_CHANNEL) {
      reportData.records.forEach((data) => {
        data.dimensionName = getL1ObjectChannelName(
          data.dimensionName,
          data.dimensionID
        );
      });
    }
    return reportData;
  }

  async getOutdoorReportData(
    dimension: ReportDimension,
    gran: ReportGran,
    filter: any,
    from: string,
    to: string
  ) {
    const filterQueryParams = Object.keys(filter)
      .map((key) => `${key}=${filter[key]}`)
      .join('&');
    let url = `report/outdoor?dimension=${dimension}&gran=${gran}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&${filterQueryParams}`;
    const response = await this.restClient.get(url);
    const reportData = wrapReportData(response.data);
    _.remove(
      reportData.allowMetrics,
      (metric) => metric === Metrics.SYS_PROFIT
    );
    if (dimension === ReportDimension.SALES_CHANNEL) {
      reportData.records.forEach((data) => {
        data.dimensionName = getL1ObjectChannelName(
          data.dimensionName,
          data.dimensionID
        );
      });
    }
    return reportData;
  }

  async getReportCompareData(
    compareDimension: ReportCompareDimension,
    filter: any,
    from: string,
    to: string
  ): Promise<ReportCompareData> {
    const filterQueryParams = Object.keys(filter)
      .map((key) => `${key}=${filter[key]}`)
      .join('&');
    const url = `report/compare-report?dimension=${ReportDimension.DAY}&gran=${ReportGran.DAY}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&compareDimension=${compareDimension}&${filterQueryParams}`;
    const response = await this.restClient.get(url);
    return wrapReportCompareData(response.data);
  }

  async downloadRebateReport(
    date: string,
    agencyName: string,
    agencyId?: string | number
  ): Promise<void> {
    const selectedDate = moment(date);
    let url = `report/export/month/reconciliation?month=${selectedDate.month() + 1}&year=${selectedDate.year()}`;
    if (agencyId) {
      url += `&agencyId=${agencyId}`;
    }
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(
      response.data,
      `MonthReport_${moment(date).format('YYYY-MM')}_${agencyName}.xlsx`
    );
  }

  async uploadFinalReportData(
    orderId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus> {
    const response = await this.restClient.post('/v2/report/final-report', {
      orderId,
      reportType
    });
    return response.data;
  }

  async getFinalReportData(
    reportId: string | number,
    reportType: FinalReportType
  ): Promise<FinalReportResponseStatus> {
    const response = await this.restClient.get(
      `/v2/report/final-report?id=${reportId}&reportType=${reportType}`
    );
    return response.data;
  }

  async downloadOrderReportRawData(
    orderId: string | number,
    startDate: string,
    endDate: string
  ): Promise<void> {
    let url = `/v2/report/export/order-report-raw-data?order_id=${orderId}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, 'OrderReportRawData.csv');
  }

  async getFilterOptions(
    type: 'VIDEO' | 'PERFORMANCE',
    gran: 'daily' | 'hour',
    dimension: string,
    currentFilters: {
      [key: string]: string | number;
    }
  ): Promise<SelectOptions[]> {
    const filterQueryParams = Object.keys(currentFilters)
      .map((key) => `${key}=${encodeURIComponent(currentFilters[key])}`)
      .join('&');
    const response = await this.restClient.get(
      `/v2/report/options?reportType=${type}&gran=${gran}&dimension=${dimension}&${filterQueryParams}`
    );
    const records = response.data.records;
    if (dimension === ReportDimension.SALES_CHANNEL) {
      return records.map((record) => ({
        ...record,
        label: getL1ObjectChannelName(record.label, record.value)
      }));
    }
    return records;
  }

  async downloadSalesChannelReconciliationReport(
    gran: string,
    salesChannelId: number,
    startDate: string,
    endDate: string
  ): Promise<void> {
    let url = `/v2/report/export/sales-channel-reconciliation?salesChannelId=${salesChannelId}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    const dateInfo =
      gran === ReportGran.MONTH
        ? moment(startDate).format('YYYY-MM')
        : `${moment(startDate).format('YYYY-MM-DD')}_${moment(endDate).format('YYYY-MM-DD')}`;
    // ECOPD-5566：檔名要**不含停用後綴**的原名，否則會下載成
    // `..._wi_fridge_停用.xlsx`。
    // 走 registry 的 `config.name` 而不是 `salesChannels.<id>` —— 後者是顯示用的，
    // 停用渠道帶後綴。registry 在登入時載入；真的查不到就退回 channelId。
    const channelName = _.snakeCase(
      (
        ChannelGroupSetting.findById(salesChannelId)?.config.name ??
        salesChannelId.toString()
      ).toLowerCase()
    );
    this.defualtFileDownload(
      response.data,
      `sales_channel_reconciliation_${dateInfo}_${channelName}.xlsx`
    );
  }

  async downloadOrderOutdoorDeviceReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void> {
    const url = `/v2/report/export/store?orderNum=${orderNumber}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(
      response.data,
      `outdoor_device_report_${orderNumber}.xlsx`
    );
  }

  async downloadOrderOutdoorDeviceBeaconReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void> {
    const url = `/v2/report/export/store-beacon?orderNum=${orderNumber}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    // 檔名由 FE 組 —— BE 送的 Content-Disposition 不在 CORS exposedHeaders 白名單裡，
    // 前端拿不到（既有狀況，見 ReportWebService 其他下載 method 的做法）。
    this.defualtFileDownload(
      response.data,
      `outdoor_device_beacon_report_${orderNumber}.xlsx`
    );
  }

  async downloadOrderBeaconEventReport(
    orderNumber: string,
    startDate: string,
    endDate: string
  ): Promise<void> {
    const url = `/v2/report/export/beacon-event?orderNum=${orderNumber}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(
      response.data,
      `line_message_report_${orderNumber}.xlsx`
    );
  }

  async getOutdoorDeviceReportData(
    l1ObjectId: number,
    from: string,
    to: string,
    metricType: OutdoorDeviceMetricType,
    gran: ReportGran,
    campaignId?: number,
    storeKeys?: string[]
  ): Promise<OutdoorDeviceReportData> {
    const params = [
      `l1ObjectId=${l1ObjectId}`,
      `from=${encodeURIComponent(from)}`,
      `to=${encodeURIComponent(to)}`,
      `metricType=${metricType}`,
      `gran=${gran}`
    ];
    // 未選活動 / 門市時整個參數省略，代表不過濾。門市用重複參數形式，值見 toStoreKey。
    if (campaignId) {
      params.push(`campaignId=${campaignId}`);
    }
    (storeKeys ?? []).forEach((storeKey) => {
      params.push(`storeKeys=${encodeURIComponent(storeKey)}`);
    });
    const response = await this.restClient.get(
      `v2/report/delivery-target/l1-object?${params.join('&')}`
    );
    return {
      ...response.data,
      records: _.get(response.data, 'records', []).map((record) => ({
        deliveryTargetName: record.deliveryTargetName,
        ...record.metrics
      }))
    };
  }

  async getWeatherVerificationCampaigns(
    l1ObjectId: number,
    from: string,
    to: string
  ): Promise<WeatherVerificationCampaignOption[]> {
    const url = `v2/report/weather-verification/campaigns?l1ObjectId=${l1ObjectId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const response = await this.restClient.get(url);
    return _.get(response.data, 'records', []);
  }

  async getWeatherVerificationStores(
    l1ObjectId: number,
    campaignId: number | undefined,
    from: string,
    to: string
  ): Promise<WeatherVerificationStoreOption[]> {
    // 不帶 campaignId 時，後端回整個廣告群組底下所有活動的門市。
    const campaignParam = campaignId ? `&campaignId=${campaignId}` : '';
    const url = `v2/report/weather-verification/stores?l1ObjectId=${l1ObjectId}${campaignParam}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const response = await this.restClient.get(url);
    return _.get(response.data, 'records', []);
  }

  async getWeatherVerificationData(
    query: WeatherVerificationQuery
  ): Promise<WeatherVerificationData> {
    const { l1ObjectId, campaignId, from, to, result, storeKeys, page } = query;
    const params = [
      `l1ObjectId=${l1ObjectId}`,
      `campaignId=${campaignId}`,
      `from=${encodeURIComponent(from)}`,
      `to=${encodeURIComponent(to)}`,
      `page=${page ?? 0}`
    ];
    // 不帶 result 代表「全部時段」——後端不吃空字串，所以未選時整個參數省略。
    if (result) {
      params.push(`result=${encodeURIComponent(result)}`);
    }
    // storeKeys 用重複參數形式；未選門市時整個參數省略代表查全部。
    (storeKeys ?? []).forEach((storeKey) => {
      params.push(`storeKeys=${encodeURIComponent(storeKey)}`);
    });
    const response = await this.restClient.get(
      `v2/report/weather-verification?${params.join('&')}`
    );
    return {
      campaign: _.get(response.data, 'campaign'),
      page: _.get(response.data, 'page', 0),
      size: _.get(response.data, 'size', 0),
      total: _.get(response.data, 'total', 0),
      records: _.get(response.data, 'records', []).map((record) => ({
        ...record,
        conditions: _.get(record, 'conditions', {}),
        unmatchedReasons: _.get(record, 'unmatchedReasons', []),
        deviceStatus: _.get(record, 'deviceStatus', {
          abnormal: false,
          noStatusData: false
        })
      }))
    };
  }

  async getWeatherVerificationSummary(
    query: WeatherVerificationSummaryQuery
  ): Promise<WeatherVerificationSummary> {
    const { l1ObjectId, campaignId, from, to, storeKeys } = query;
    const params = [
      `l1ObjectId=${l1ObjectId}`,
      `campaignId=${campaignId}`,
      `from=${encodeURIComponent(from)}`,
      `to=${encodeURIComponent(to)}`
    ];
    // 與明細相同：storeKeys 用重複參數，未選門市時整個參數省略代表查全部。
    (storeKeys ?? []).forEach((storeKey) => {
      params.push(`storeKeys=${encodeURIComponent(storeKey)}`);
    });
    // 統整卡失敗只在卡片區提示、不能擋明細，所以不走全域錯誤處理：
    // 它遇到錯誤會跳 toast，404 還會把整頁換成錯誤頁
    const response = await this.quietClient.get(
      `v2/report/weather-verification/summary?${params.join('&')}`
    );
    return response.data;
  }
}

const wrapReportData = (json: any) => {
  return {
    allowMetrics: _.get(json, 'allowMetrics', []),
    dimension: _.get(json, 'dimension'),
    filter: _.get(json, 'filter', {}),
    from: _.get(json, 'from'),
    to: _.get(json, 'to'),
    records: _.get(json, 'records', []).map((record) => wrapRecord(record)),
    currency: _.get(json, 'currency')
  };
};

const wrapReportCompareData = (json: any) => {
  return {
    allowMetrics: _.get(json, 'allowMetrics', []),
    dimension: _.get(json, 'dimension'),
    filter: _.get(json, 'filter', {}),
    from: _.get(json, 'from'),
    to: _.get(json, 'to'),
    records: _.get(json, 'records', []).map((record) =>
      wrapCompareRecord(record)
    ),
    currency: _.get(json, 'currency')
  };
};

const wrapRecord = (json: any) => {
  return {
    ...json,
    dimensionID: _.get(json, 'dimensionID'),
    dimensionName: _.get(json, 'dimensionName'),
    agcProfit: _.get(json, 'agcProfit'),
    clicks: _.get(json, 'clicks'),
    complete: _.get(json, 'complete'),
    conv_1: _.get(json, 'conv_1'),
    conv_7: _.get(json, 'conv_7'),
    convs: _.get(json, 'convs'),
    dataCost: _.get(json, 'dataCost'),
    firstQuartile: _.get(json, 'firstQuartile'),
    midpoint: _.get(json, 'midpoint'),
    thirdQuartile: _.get(json, 'thirdQuartile'),
    impres: _.get(json, 'impres'),
    incentive: _.get(json, 'incentive'),
    mediaCost: _.get(json, 'mediaCost'),
    mediaSpent: _.get(json, 'mediaSpent'),
    skipCount: _.get(json, 'skipCount'),
    spent: _.get(json, 'spent'),
    sysProfit: _.get(json, 'sysProfit'),
    underBidFloor: _.get(json, 'underBidFloor'),
    view: _.get(json, 'view'),
    viewable: _.get(json, 'viewable'),
    winTotal: _.get(json, 'winTotal'),
    tags: _.compact(_.get(json, 'tags', '').split(',')),
    parentId: _.get(json, 'parentId')
  };
};

const wrapCompareRecord = (json: any) => {
  const campareID = _.get(json, 'compareID');
  const compareName = _.get(json, 'compareName');
  return {
    ...wrapRecord(json),
    compareID: campareID ? campareID : compareName,
    compareName
  };
};
